/// <amd-module name="Test/TestPlansHubRefresh/TestHub/Component/TestPlansHubRefreshConnectedView" />
import * as React from "react";
import { connect } from "react-redux";
import { Dispatch } from "redux";
import * as Reselect from "Reselect";
import { PreviewSplitterOptions, TelemetryConstants, TestHubRefreshConstants } from "Test/TestPlansHubRefresh/TestHub/Constants";
import { TelemetryActions } from "Test/TestPlansHubRefresh/TestHub/Redux//TelemetryActions";
import { SuitesActions } from "Test/TestPlansHubRefresh/TestHub/Redux/Suites/SuitesActions";
import { ISuitesAwareState, TestSuiteWithCount } from "Test/TestPlansHubRefresh/TestHub/Redux/Suites/SuitesContract";
import { TestHubRefreshActions } from "Test/TestPlansHubRefresh/TestHub/Redux/TestHubRefresh/TestHubRefreshActions";
import * as Resources from "Test/TestPlansHubRefresh/TestHub/Resources";
import { getDataProviderContent, setTestPlanPageTitle } from "Test/TestPlansHubRefresh/TestHub/Util";
import {
    initializeTestHubLocalContext,
    TestHubRefreshLocalContext,
    testHubRefreshLocalContextData
} from "Test/TestPlansHubRefresh/TestHub/Utils/TestPlansHubRefreshLocalContextProvider";
import { TestSuite, TestSuiteType } from "TestPlanCommon/Contracts/TestPlan";
import { ITfsPageService } from "Tfs/Platform/Page";
import { IReadonlyObservableArray, ObservableCollection, ObservableValue } from "VSS/Core/Observable";
import { ScreenSize } from "VSS/Core/Util/Screen";
import { localeIgnoreCaseComparer } from "VSS/Core/Util/String";
import { addTabs, ILegacyContributedTab } from "VSS/Features/PlatformUI/ContributedTab";
import { FastPageSwitchEventDetail } from "VSS/Platform/Context";
import { IVssContributionService } from "VSS/Platform/Contribution";
import { isFeatureEnabled, isFeatureFlagEnabled } from "VSS/Platform/Feature";
import { FastPageSwitch } from "VSS/Platform/FPS";
import { IVssHistoryService } from "VSS/Platform/History";
import { css, IVssComponentContext, IVssComponentProperties, IVssLayoutManager, VssComponent, WrappedComponent } from "VSS/Platform/Layout";
import { IVssNavigationService } from "VSS/Platform/Navigation";
import { IVssPerformanceService } from "VSS/Platform/Performance";
import { ISettingsService, SettingsUserScope } from "VSS/Platform/Settings";
import { IQueryParameter, Uri } from "VSS/Platform/Util/Url";
import { Button } from "VSSUI/Button";
import { Header, TitleSize } from "VSSUI/Header";
import { IHeaderCommandBarItem } from "VSSUI/HeaderCommandBar";
import { Icon } from "VSSUI/Icon";
import { Link } from "VSSUI/Link";
import { Observer } from "VSSUI/Observer";
import { Page } from "VSSUI/Page";
import { ConnectedHeaderCommandBar } from "VSSUI/ReduxHeaderCommandBar";
import { Spinner, SpinnerSize } from "VSSUI/Spinner";
import { Splitter, SplitterDirection, SplitterElementPosition } from "VSSUI/Splitter";
import { IVssContributedTab, TabBar, TabContent, TabSize } from "VSSUI/Tabs";
import { ScreenSizeObserver } from "VSSUI/Utilities/ScreenSize";
import { TestPlansHubRefreshLeftView } from "./TestPlansHubRefreshLeftView";

export interface ITestPlansHubRefreshOwnProps extends IVssComponentProperties {
    /**
     * Currently selected tab
     */
    selectedTab: ObservableValue<string>;
    // opId in the url for test plan cloning
    opId: number | undefined;
}

export interface ITestPlansHubRefreshMappedProps {
    testPlanId: number;
    testPlanName: string;
    areaPath: string;
    iterationPath: string;
    rootSuiteId: number;
    mineAllPageUrl: string;
    suiteType: TestSuiteType;
    testSuites: TestSuite[];
    selectedSuiteId: number;
    selectedSuiteName: string;
    isLoading: boolean;
    isBusy: boolean;
    isAdvancedLicenseEnabled: boolean;
    isInGridView: boolean;
    isGridDirty: boolean;
    splitterWidth: number;
    idToTestSuiteMap: Map<number, TestSuiteWithCount>;
    isShowTestsFromChildSuitesEnabled: boolean;
    isPreviewEnabled: boolean;
    publishTelemetry(action: string, payload: { [key: string]: any }): void;
    setGridView(isInGridView: boolean): void;
    reloadTestCasesForSelectedSuite(planId: number, selectedSuite: number, selectedSuiteName: string): void;
    editColumnOptions(planId: number, selectedSuite: number): void;
    togglePreview(previewStatus: string): void;
    showSuiteLevelGrid(planId: number, suiteId: number, planName: string, areaPath: string, iterationPath: string): void;
    saveSplitterWidth(splitterWidth: number): void;
    onExportTestCasesClick(planId: number, selectedSuiteId: number, planName: string, suiteName: string): void;
    onExportTestCasesToExcelClick(planId: number, selectedSuiteId: number, planName: string, suiteName: string): void;
    closeGrid(planId: number, selectedSuiteId: number): void;
    getMineAllPageUrl(): void;
}

/**
 * TestPlansHubRefreshView: This is the core root view component wrapped by the portal component.
 * This view renders the suites tree on the left pane and the define/execute pivots on the right pane.
 */
export class TestPlansHubRefreshView extends VssComponent<ITestPlansHubRefreshMappedProps & ITestPlansHubRefreshOwnProps> {
    private _pivotProvider: IReadonlyObservableArray<IVssContributedTab>;
    private _showImportPanel: ObservableValue<boolean>;
    private _showCloneProgressToast: ObservableValue<boolean>;
    private _isExtraSmallWidth: ObservableValue<boolean>;

    constructor(props: ITestPlansHubRefreshMappedProps & ITestPlansHubRefreshOwnProps, context?: IVssComponentContext) {
        super(props, context);
        initializeTestHubLocalContext(this.props.selectedTab.value);
        this._setContributedProvider();
        this._showImportPanel = new ObservableValue<boolean>(false);
        this._isExtraSmallWidth = new ObservableValue<boolean>(true);
        this._showCloneProgressToast = new ObservableValue<boolean>(false);
        this._handleNavigateAway = this._handleNavigateAway.bind(this);
    }

    public componentDidMount() {
        setTestPlanPageTitle(this.context.pageContext, `${Resources.TestPlanText} ${this.props.testPlanId} ${this.props.testPlanName}`);
        document.body.addEventListener("fpsStarting", this._handleNavigateAway);
        window.addEventListener("beforeunload", this._handleNavigateAway);
        this.props.getMineAllPageUrl();
        const contributionService: IVssContributionService = this.context.pageContext.getService<IVssContributionService>("IVssContributionService");
        contributionService.getContributionAsync("ms.vss-test-web.breadcrumb-service").then(() => {
            const breadcrumbService = this.context.pageContext.getService("testplans-hub-refresh-breadcrumb-service") as any;
            breadcrumbService.addBreadCrumb("HubGroup", "Test Plans", this.props.mineAllPageUrl, TestHubRefreshConstants.BREADCRUMB_PRIRORITY);
            breadcrumbService.addBreadCrumb("Hub", this.props.testPlanName, undefined, TestHubRefreshConstants.BREADCRUMB_PRIRORITY);
        });
    }

    public componentDidUpdate() {
        this._adjustSuiteTreeHeight();
    }

    public componentWillUnmount() {
        document.body.removeEventListener("fpsStarting", this._handleNavigateAway);
        window.removeEventListener("beforeunload", this._handleNavigateAway);
    }

    private _handleNavigateAway = (e: BeforeUnloadEvent | CustomEvent) => {
        if (this.props.isInGridView && this.props.isGridDirty) {
            // Handle cases of Fast Page Switching
            const fpsDetail = (e as CustomEvent).detail as FastPageSwitchEventDetail;
            if (fpsDetail && fpsDetail.href) {
                if (fpsDetail.data && fpsDetail.data.isPageSwitchConfirmed) {
                    return;
                }

                e.preventDefault();
                if (confirm(Resources.UnsavedChangesMessage)) {
                    this._closeGrid();
                    fpsDetail.data = fpsDetail.data || {};
                    fpsDetail.data.isPageSwitchConfirmed = true;
                    FastPageSwitch(fpsDetail.pageContext, fpsDetail.href, fpsDetail.recordHistoryEntry, fpsDetail.data);
                }
            } else {
                // Handle cases of general browser BeforeUnloadEvent
                e.returnValue = true;
            }
        }
    };

    public render(): JSX.Element {
        const { selectedTab } = this.props;
        const layoutManager = this.context.pageContext.getService<IVssLayoutManager>("IVssLayoutManager");
        if (this.props.opId && this.props.opId > 0) {
            this._showCloneProgressToast.value = true;
        }
        let testPlansBusyView: JSX.Element = (<></>) as JSX.Element;
        if (this.props.isBusy) {
            // This spinner is shown when the view is busy due to some user operation
            testPlansBusyView = (
                <div className="testplans-hub-refresh-busy-view">
                    <Spinner className="testplans-spinner-pos flex-grow" size={SpinnerSize.large} />
                </div>
            );
        }
        return (
            <>
                <TestHubRefreshLocalContext.Provider value={testHubRefreshLocalContextData}>
                    <>
                        <Page className="testplans-hub-refresh-page" tabProviders={this._pivotProvider} selectedTabId={selectedTab}>
                            <Observer fullScreenMode={layoutManager.getFullScreenMode()} isExtraSmallWidth={this._isExtraSmallWidth}>
                                {(displayProps: { fullScreenMode: boolean; isExtraSmallWidth: boolean }) => (
                                    //Observes changes in screen size (browser resolution) and realigns contents to avoid multi-directional scrollbars even at a resolution of 320x256.
                                    <ScreenSizeObserver>
                                        {(screenSizeProps: { screenSize: ScreenSize }) => {
                                            //true when browser is zoomed to 250% (for screen resolution 1280x1024) or greater
                                            //trigger zoom level will be different for different resolutions.
                                            const isExtraSmallWidth = screenSizeProps.screenSize === ScreenSize.xsmall;
                                            this._isExtraSmallWidth.value = isExtraSmallWidth;
                                            this._adjustSuiteTreeHeight();
                                            return (
                                                <Splitter
                                                    splitterDirection={isExtraSmallWidth ? SplitterDirection.Horizontal : SplitterDirection.Vertical}
                                                    nearElementClassName="flex flex-column"
                                                    farElementClassName="test-hub-refresh-far-content"
                                                    fixedElement={SplitterElementPosition.Near}
                                                    fixedSize={this.props.splitterWidth !== undefined ? this.props.splitterWidth : 320}
                                                    minFixedSize={isExtraSmallWidth ? 0.3 * window.innerHeight : 0.04 * window.innerWidth}
                                                    maxFixedSize={isExtraSmallWidth ? 0.4 * window.innerHeight : 0.8 * window.innerWidth}
                                                    className="test-plans-hub-refresh-splitter"
                                                    onRenderFarElement={this._renderFarContent.bind(this)}
                                                    onRenderNearElement={displayProps.fullScreenMode ? undefined : this._renderNearContent.bind(this)}
                                                    onFixedSizeChanged={(newFixedSize: number) => {
                                                        if (!isExtraSmallWidth) {
                                                            this._adjustTestplanDropdownMaxWidth(newFixedSize);
                                                        } else {
                                                            this._adjustSuiteTreeHeight();
                                                        }
                                                        this.props.saveSplitterWidth(newFixedSize);
                                                    }}
                                                />
                                            );
                                        }}
                                    </ScreenSizeObserver>
                                )}
                            </Observer>
                            <Observer showToast={this._showCloneProgressToast}>
                                {(props: { showToast: boolean }) => {
                                    return (
                                        props.showToast && (
                                            <WrappedComponent
                                                dependencies={["ms.vss-test-web.testplan-clone-progress"]}
                                                wrappedType={"testplan-clone-progress"}
                                                wrappedProps={{
                                                    opId: this.props.opId,
                                                    planId: this.props.testPlanId
                                                }}
                                            />
                                        )
                                    );
                                }}
                            </Observer>
                        </Page>
                        {testPlansBusyView}
                    </>
                </TestHubRefreshLocalContext.Provider>
            </>
        );
    }

    public shouldComponentUpdate(nextProps: ITestPlansHubRefreshMappedProps & ITestPlansHubRefreshOwnProps) {
        const historyService = this.context.pageContext.getService<IVssHistoryService>("IVssHistoryService");
        const currentState = historyService.getState();
        if (currentState) {
            if (
                currentState[TestHubRefreshConstants.QUERY_PARAM_PLAN_ID] !== nextProps.testPlanId!.toString() ||
                currentState[TestHubRefreshConstants.QUERY_PARAM_SUITE_ID] !== nextProps.selectedSuiteId!.toString() ||
                currentState[TestHubRefreshConstants.PIVOT_ID] !== nextProps.selectedTab.value
            ) {
                currentState[TestHubRefreshConstants.QUERY_PARAM_PLAN_ID] = nextProps.testPlanId!.toString();
                currentState[TestHubRefreshConstants.QUERY_PARAM_SUITE_ID] = nextProps.selectedSuiteId!.toString();
                currentState[TestHubRefreshConstants.PIVOT_ID] = nextProps.selectedTab.value;
                historyService.pushState(currentState);
                this._setContributedProvider();
            }
        }
        return true;
    }

    // Render the suite tree
    public _renderNearContent(): JSX.Element {
        return <TestPlansHubRefreshLeftView />;
    }

    // Render the right side content of the splitter
    public _renderFarContent(): JSX.Element {
        return (
            <>
                <div className="testplans-hub-refresh-rhs-container flex-grow">
                    <Header
                        className="testplans-hub-refresh-far-content-header title-s"
                        title={this.props.selectedSuiteName + " (" + Resources.ID + ": " + this.props.selectedSuiteId + ")"}
                        titleSize={TitleSize.Medium}
                    >
                        <Link
                            className="help-link-button"
                            onClick={this._openHelpInfo}
                            ariaLabel={Resources.HelpLinkAriaLabelForNewTestPlansPage}
                            role="link"
                            removeUnderline={true}
                        >
                            <span className="help-link-icon bowtie-icon bowtie-status-help-outline"></span>
                            <span>{Resources.Help}</span>
                        </Link>
                    </Header>
                    <TabBar
                        className="testplans-hub-refresh-tab-bar"
                        onSelectedTabChanged={this._onSelectedTabChanged}
                        tabSize={TabSize.Tall}
                        renderAdditionalContent={this._getPivotBarItems.bind(this)}
                    />
                    <TabContent />
                </div>
                <Observer showPanel={this._showImportPanel}>
                    {(props: { showPanel: boolean }) => {
                        return (
                            props.showPanel && (
                                <WrappedComponent
                                    dependencies={["ms.vss-test-web.testcases-import-module"]}
                                    wrappedType={"testcases-import-panel"}
                                    wrappedProps={{
                                        showPanel: this._showImportPanel,
                                        planId: this.props.testPlanId,
                                        areaPath: this.props.areaPath,
                                        iterationPath: this.props.iterationPath,
                                        suite: this.props.idToTestSuiteMap.get(this.props.selectedSuiteId)
                                    }}
                                />
                            )
                        );
                    }}
                </Observer>
            </>
        );
    }

    /**
     * This renders the close button when in Grid View and the filter button on the pivot bar.
     * The actual filter bar is implementated in a separate component
     */
    private _getPivotBarItems(): JSX.Element {
        const showFilter =
            localeIgnoreCaseComparer(this.props.selectedTab.value, TestHubRefreshConstants.DEFINE_TAB_ID) === 0 ||
            localeIgnoreCaseComparer(this.props.selectedTab.value, TestHubRefreshConstants.EXECUTE_TAB_ID) === 0;

        if (this.props.isInGridView == true) {
            return (
                <Button
                    className="testplans-header-command-bar"
                    text={Resources.CloseGrid}
                    primary={true}
                    onClick={() => {
                        if (this.props.isGridDirty === true) {
                            if (confirm(Resources.UnsavedChangesMessage)) {
                                this._closeGrid();
                            }
                        } else {
                            this._closeGrid();
                        }
                    }}
                />
            );
        } else {
            return (
                <div className="testplans-header-command-bar flex-row h-scroll-auto custom-scrollbar">
                    <ConnectedHeaderCommandBar showFilter={showFilter} commandsSelector={CommandSelector} viewActionsSelector={this._viewActions} />
                </div>
            );
        }
    }

    private _closeGrid() {
        this.props.closeGrid(this.props.testPlanId, this.props.selectedSuiteId);
        this.props.reloadTestCasesForSelectedSuite(this.props.testPlanId, this.props.selectedSuiteId, this.props.selectedSuiteName);
    }

    private _setContributedProvider() {
        const navigationService = this.context.pageContext.getService<IVssNavigationService>("IVssNavigationService");
        const routeValues = navigationService.getCurrentRoute().routeValues;

        // This will make sure all the query parameters in the url are preserved on tab change
        const url = new Uri(window.location.href);
        const queryParams = url.queryParameters;
        const params: { [param: string]: string } = {};
        if (queryParams && queryParams.length > 0) {
            queryParams.forEach((param: IQueryParameter) => {
                if (param.name && param.value) {
                    params[param.name] = param.value;
                }
            });
        }
        const providerRouteValues = {
            project: routeValues["project"],
            ...params
        };

        const data = getDataProviderContent(this.context.pageContext);
        let id;
        if (data.isAdvancedExtensionEnabled) {
            id = "ms.vss-test-web.testplans-hub-refresh:pivots";
        } else {
            id = "ms.vss-test-web.testplans-hub-refresh:pivots-limited";
        }

        const tabsCollection = new ObservableCollection<IVssContributedTab>();
        // adding the default tabs - define, execute, charts
        addTabs(this.context.pageContext, tabsCollection, id, { routeValues: providerRouteValues, updateUrlOnPivotChange: true });

        if (isFeatureFlagEnabled(this.context.pageContext, TestHubRefreshConstants.EnableThirdPartyTabContributionsFF, false)) {
            let selectedSuiteId = this.props.selectedSuiteId;
            /**
             * selectedSuiteId is fetched from url to provide the latest suite id for the 3rd party tab context.
             * tabs are added before this.props.selectedSuiteId is updated so suiteId from url is used.
             *
             **/
            if (params["suiteId"]) {
                selectedSuiteId = Number(params["suiteId"]);
            }
            const selectedSuite = this.props.idToTestSuiteMap.get(selectedSuiteId) as TestSuiteWithCount;
            if (selectedSuite) {
                const pivotProviderOptions = {
                    updateUrlOnPivotChange: true,
                    // contextual data is required for the 3rd party extensions contributing to tabs
                    contextualData: {
                        selectedPlan: {
                            id: this.props.testPlanId,
                            name: this.props.testPlanName,
                            areaPath: this.props.areaPath,
                            iteration: this.props.iterationPath,
                            rootSuiteId: this.props.rootSuiteId
                        },
                        selectedSuite: {
                            id: selectedSuite.id,
                            name: selectedSuite.name,
                            suiteType: this.props.suiteType,
                            pointCount: selectedSuite.pointCount,
                            parentSuiteId: selectedSuite.parentSuite ? selectedSuite.parentSuite.id : 0,
                            queryText: selectedSuite.queryString,
                            requirementId: selectedSuite.requirementId,
                            revision: selectedSuite.revision
                        },
                        showChildSuites: this.props.isShowTestsFromChildSuitesEnabled
                    },
                    preProcessTabContribution: (tab: IVssContributedTab | ILegacyContributedTab): void => {
                        // below statements are used to assign render function of tab as <VssLegacyContributionContentHost> (check ContributedTab.tsx)
                        tab.render = undefined;
                        (tab as any).content = "legacyContributionContent";
                        /**
                         *  tabId of the 3rd party extension is used as pivot in the url ({project}/_testPlans/{pivots}) in case of tab change.
                         *  this provides the functionality to load the same extension tab on reload if tabId is mentioned the url.
                         * */
                        tab.routeId = "ms.vss-test-web.testplans-hub-refresh-route";
                        tab.routeValues = {
                            project: routeValues["project"],
                            pivots: this.props.selectedTab.value
                        };
                    }
                };
                // adding 3rd party tabs which are targetting to id - test-plan-pivot-tabs
                addTabs(this.context.pageContext, tabsCollection, "ms.vss-test-web.test-plan-pivot-tabs", pivotProviderOptions);
            }
        }
        this._pivotProvider = tabsCollection;
    }

    private _onSelectedTabChanged = (pivotId: string) => {
        if (!this.props.isInGridView || !this.props.isGridDirty || confirm(Resources.UnsavedChangesMessage)) {
            this.props.selectedTab.value = pivotId;
            testHubRefreshLocalContextData.setCurrentPivot(pivotId);

            const data = getDataProviderContent(this.context.pageContext);

            // publish pivot change telemetry
            const telemetryPayload = {
                newPivot: pivotId,
                isAdvancedLicenseEnabled: data.isAdvancedExtensionEnabled
            };
            this.props.publishTelemetry(TelemetryConstants.PIVOT_CHANGED, telemetryPayload);

            // setting default view to list
            // this is required when we move from grid view in author tab to execute tab
            // and come back to author tab.
            this.props.setGridView(false);

            let registryEntries = {
                SelectedPivotId: pivotId
            };
            const settingsService: ISettingsService = this.context.pageContext.getService<ISettingsService>("ISettingsService");
            const tfsPageService: ITfsPageService = this.context.pageContext.getService<ITfsPageService>("ITfsPageService");
            const tfsPageData = tfsPageService.getData();

            if (tfsPageData && tfsPageData.project) {
                settingsService.setEntries(registryEntries, SettingsUserScope.Me, "project", tfsPageData.project.id);
            }

            // if tab is 3rd party tab, pivotId needs to be updated for the url and 3rd party tabs are updated with the new contextual data
            if (
                pivotId !== TestHubRefreshConstants.DEFINE_TAB_ID &&
                pivotId !== TestHubRefreshConstants.EXECUTE_TAB_ID &&
                pivotId !== TestHubRefreshConstants.CHART_TAB_ID
            ) {
                const historyService = this.context.pageContext.getService<IVssHistoryService>("IVssHistoryService");
                const currentState = historyService.getState();
                currentState[TestHubRefreshConstants.PIVOT_ID] = pivotId;
                historyService.pushState(currentState);
                this._setContributedProvider();
            }
            // start the non-page load TTI telemetry scenario here, this will end in the target pivots component
            let scenarioName: string | undefined = undefined;
            if (localeIgnoreCaseComparer(pivotId, TestHubRefreshConstants.DEFINE_TAB_ID) === 0) {
                scenarioName = TelemetryConstants.AUTHOR_PIVOT_PAGE_LOAD_TTI_SCENARIO;
            } else if (localeIgnoreCaseComparer(pivotId, TestHubRefreshConstants.EXECUTE_TAB_ID) === 0) {
                scenarioName = TelemetryConstants.EXECUTE_PIVOT_PAGE_LOAD_TTI_SCENARIO;
            }

            if (scenarioName) {
                const properties = {
                    ttiScenario: "PivotChanged"
                };
                const performanceService: IVssPerformanceService =
                    this.context.pageContext.getService<IVssPerformanceService>("IVssPerformanceService");
                performanceService.startScenario(scenarioName, true, undefined, properties);
            }
        }

        //calling close grid to update suite count and reload suite when user navigates away from grid by clicking on define tab
        if (this.props.isInGridView && pivotId === TestHubRefreshConstants.DEFINE_TAB_ID) {
            this._closeGrid();
        }
    };

    private _adjustTestplanDropdownMaxWidth(sectionSize: number) {
        // Update the width of the plan selector button if the total width is changed using the splitter.
        const dropdownButtonMatches: any = document.getElementsByClassName("testplan-dropdown-button");
        if (dropdownButtonMatches && dropdownButtonMatches[0] && dropdownButtonMatches[0].children && dropdownButtonMatches[0].children[0]) {
            dropdownButtonMatches[0].children[0].setAttribute("style", "max-width: " + (sectionSize - 135) + "px");
            // Note that the number being subtracted would have to be changed if the structure of the section containing the plan selector, favorite. etc. is changed.
        }
    }

    private _adjustSuiteTreeHeight() {
        // Update the height of the nearElement when the splitter direction changes.
        const nearElement: any = document.getElementsByClassName("testplans-hub-left-view");
        const testPlanSegmentMatches: any = document.getElementsByClassName("testplans-segment");
        const suiteTreeSegmentElement: any = document.getElementsByClassName("suite-tree-segment");
        if (nearElement && nearElement[0] && testPlanSegmentMatches && testPlanSegmentMatches[0]) {
            const containerHeight = nearElement[0].offsetHeight;
            const firstChildHeight = testPlanSegmentMatches[0].offsetHeight;

            if (this._isExtraSmallWidth.value === false) {
                suiteTreeSegmentElement[0]?.setAttribute("style", "height: " + (containerHeight - firstChildHeight) + "px");
            } else {
                suiteTreeSegmentElement[0]?.setAttribute("style", "height: " + "auto");
            }
        }

        // Update the height of the suite tree when the splitter direction changes.
        const suiteTreeContainer: any = document.getElementsByClassName("testhub-refresh-tree-container");
        const suiteTreeHeaderMatches: any = document.getElementsByClassName("testplans-hub-refresh-near-content-header");
        if (suiteTreeContainer && suiteTreeContainer[0] && suiteTreeHeaderMatches && suiteTreeHeaderMatches[0]) {
            const suiteTreeElement: any = document.getElementsByClassName("testhub-refresh-tree");
            const containerHeight = suiteTreeContainer.offsetHeight;
            const firstChildHeight = suiteTreeHeaderMatches[0].offsetHeight;

            if (suiteTreeElement && suiteTreeElement[0]) {
                suiteTreeElement[0]?.setAttribute("style", "max-height: " + (containerHeight - firstChildHeight) + "px");
            }
        }
    }

    private _viewActions = (): IHeaderCommandBarItem[] => {
        const tabBarCommands: IHeaderCommandBarItem[] = [];
        if (testHubRefreshLocalContextData.getCurrentPivot() === TestHubRefreshConstants.DEFINE_TAB_ID) {
            tabBarCommands.push({
                id: "export-test-cases",
                iconProps: {
                    iconName: "Download",
                    className: "export-icon"
                },
                subtle: true,
                tooltipProps: {
                    text: Resources.ExportTestCases
                },
                important: true,
                subMenuProps: {
                    id: "export-test-cases-submenu",
                    items: [
                        {
                            id: "export-test-cases-to-csv",
                            text: Resources.ExportTestCasesToCSV,
                            onActivate: () => {
                                this.props.onExportTestCasesClick(
                                    this.props.testPlanId,
                                    this.props.selectedSuiteId,
                                    this.props.testPlanName,
                                    this.props.selectedSuiteName
                                );
                                const telemetryPayload = {
                                    planId: this.props.testPlanId,
                                    suiteId: this.props.selectedSuiteId,
                                    fileType: "csv"
                                };
                                this.props.publishTelemetry(TelemetryConstants.CLICK_EXPORT_TESTCASES, telemetryPayload);
                            }
                        },
                        {
                            id: "export-test-cases-to-excel",
                            text: Resources.ExportTestCasesToXLSX,
                            onActivate: () => {
                                this.props.onExportTestCasesToExcelClick(
                                    this.props.testPlanId,
                                    this.props.selectedSuiteId,
                                    this.props.testPlanName,
                                    this.props.selectedSuiteName
                                );
                                const telemetryPayload = {
                                    planId: this.props.testPlanId,
                                    suiteId: this.props.selectedSuiteId,
                                    fileType: "xlsx"
                                };
                                this.props.publishTelemetry(TelemetryConstants.CLICK_EXPORT_TESTCASES, telemetryPayload);
                            }
                        }
                    ]
                }
            });

            if (this.props.suiteType !== TestSuiteType.DynamicTestSuite) {
                tabBarCommands.push({
                    id: "import-test-cases",
                    iconProps: {
                        iconName: "Upload",
                        className: "import-icon"
                    },
                    subtle: true,
                    onActivate: () => {
                        this._showImportPanel.value = true;
                        const telemetryPayload = {
                            planId: this.props.testPlanId,
                            suiteId: this.props.selectedSuiteId,
                            areaPath: this.props.areaPath,
                            iterationPath: this.props.iterationPath
                        };
                        this.props.publishTelemetry(TelemetryConstants.CLICK_IMPORT_TESTCASES, telemetryPayload);
                    },
                    tooltipProps: {
                        text: Resources.ImportTestCasesCsvOrExcel
                    },
                    important: true
                });

                tabBarCommands.push({
                    ariaLabel: Resources.GridView,
                    id: "view-toggle",
                    iconProps: {
                        iconName: "GridViewSmall"
                    },
                    subtle: true,
                    onActivate: () => {
                        this.props.setGridView(true);
                        this.props.showSuiteLevelGrid(
                            this.props.testPlanId,
                            this.props.selectedSuiteId,
                            this.props.testPlanName,
                            this.props.areaPath,
                            this.props.iterationPath
                        );
                    },
                    className: "view-toggle-button",
                    tooltipProps: {
                        text: Resources.GridView
                    },
                    important: true
                });
            }
        }

        if (
            localeIgnoreCaseComparer(this.props.selectedTab.value, TestHubRefreshConstants.DEFINE_TAB_ID) === 0 ||
            localeIgnoreCaseComparer(this.props.selectedTab.value, TestHubRefreshConstants.EXECUTE_TAB_ID) === 0
        ) {
            if (isFeatureEnabled(this.context.pageContext, "ms.vss-work-web.new-boards-hub-feature", false)) {
                tabBarCommands.push({
                    ariaLabel: Resources.TogglePreviewPane,
                    id: "preview-pane",
                    iconProps: {
                        iconName: "DockRight"
                    },
                    subtle: !this.props.isPreviewEnabled,
                    onActivate: () => {
                        this.props.isPreviewEnabled
                            ? this.props.togglePreview(PreviewSplitterOptions.OFF)
                            : this.props.togglePreview(PreviewSplitterOptions.RIGHT);
                    },
                    className: "toggle-preview-button",
                    tooltipProps: {
                        text: Resources.TogglePreviewPane
                    },
                    important: true
                });
            }
            tabBarCommands.push({
                ariaLabel: Resources.ColumnOptions,
                id: "column-options",
                iconProps: {
                    iconName: "TripleColumnEdit"
                },
                subtle: true,
                onActivate: () => {
                    this.props.editColumnOptions(this.props.testPlanId, this.props.selectedSuiteId);
                },
                className: "column-options-button",
                tooltipProps: {
                    text: Resources.ColumnOptions
                },
                important: true
            });
        }

        tabBarCommands.push({
            ariaLabel: Resources.ToggleFullScreenMode,
            iconProps: {
                render: (className?: string) => {
                    const layoutManager = this.context.pageContext.getService<IVssLayoutManager>("IVssLayoutManager");
                    return (
                        <Observer fullScreenMode={layoutManager.getFullScreenMode()}>
                            {(props: { fullScreenMode: boolean }) => (
                                <Icon className={css(className, "medium")} iconName={props.fullScreenMode ? "BackToWindow" : "FullScreen"} />
                            )}
                        </Observer>
                    );
                }
            },
            id: "fullscreen",
            onActivate: () => {
                const layoutManager = this.context.pageContext.getService<IVssLayoutManager>("IVssLayoutManager");
                layoutManager.setFullScreenMode(!layoutManager.getFullScreenMode().value);
            },
            subtle: true,
            tooltipProps: { text: Resources.ToggleFullScreenMode },
            important: true
        });

        return tabBarCommands;
    };

    private _openHelpInfo = () => {
        let newWindow = window.open(TestHubRefreshConstants.DocLinkNewTestPlansPage, "_blank");
        if (newWindow && newWindow.opener) {
            newWindow.opener = null;
        }
        // publish telemetry when help button clicked
        const telemetryPayload = {
            currentPivot: this.props.selectedTab.value,
            isAdvancedLicenseEnabled: this.props.isAdvancedLicenseEnabled,
            testPlanId: this.props.testPlanId
        };
        this.props.publishTelemetry(TelemetryConstants.HELP_TEST_PLANS_PAGE, telemetryPayload);
    };
}

function mapDispatchToProps(dispatch: Dispatch): Partial<ITestPlansHubRefreshMappedProps> {
    return {
        publishTelemetry: (action: string, payload: { [key: string]: any }) => dispatch(TelemetryActions.publishTelemetry(action, payload)),
        setGridView: (isInGridView: boolean) => dispatch(TestHubRefreshActions.setGridView(isInGridView)),
        editColumnOptions: (planId: number, selectedSuiteId: number) =>
            dispatch(TestHubRefreshActions.editColumnOptionFieldsClicked(planId, selectedSuiteId)),
        togglePreview: (previewStatus: string) => dispatch(TestHubRefreshActions.saveTestCasePreviewStatus(previewStatus)),
        reloadTestCasesForSelectedSuite: (planId: number, selectedSuiteId: number, selectedSuiteName: string) =>
            dispatch(SuitesActions.changeSelectedSuite(planId, selectedSuiteId, selectedSuiteName)),
        showSuiteLevelGrid: (planId: number, suiteId: number, planName: string, areaPath: string, iterationPath: string) =>
            dispatch(TestHubRefreshActions.showSuiteLevelGrid(planId, suiteId, planName, areaPath, iterationPath)),
        saveSplitterWidth: (splitterWidth: number) => dispatch(SuitesActions.saveSplitterWidth(splitterWidth)),
        onExportTestCasesClick: (planId: number, selectedSuiteId: number, planName: string, suiteName: string) =>
            dispatch(SuitesActions.exportTestCasesToCSV(planId, selectedSuiteId, planName, suiteName)),
        onExportTestCasesToExcelClick: (planId: number, selectedSuiteId: number, planName: string, suiteName: string) =>
            dispatch(SuitesActions.exportTestCasesToExcel(planId, selectedSuiteId, planName, suiteName)),
        closeGrid: (planId: number, selectedSuiteId: number) => dispatch(TestHubRefreshActions.closeGrid(planId, selectedSuiteId)),
        getMineAllPageUrl: () => dispatch(SuitesActions.getMineAllPageUrl())
    };
}

export const CommandSelector = Reselect.createSelector(
    // Just a placeholder for now; the ConnectedHeaderCommandBar requires this but we don't have any commands
    [() => {}],
    () => {
        return [];
    }
);

function mapStateToProps(state: ISuitesAwareState, props: ITestPlansHubRefreshOwnProps): Partial<ITestPlansHubRefreshMappedProps> {
    let suite = state.suiteState.idToTestSuiteMap.get(state.testHubRefreshState.selectedSuiteId);
    let suiteType: TestSuiteType = TestSuiteType.None;
    let previewStatus: string = state.testHubRefreshState.testCasePreviewStatus;
    if (suite) {
        suiteType = suite.suiteType;
    }

    return {
        testPlanId: state.testHubRefreshState.testPlanId,
        testPlanName: state.testHubRefreshState.testPlanName,
        areaPath: state.testHubRefreshState.testPlanAreaPath,
        iterationPath: state.testHubRefreshState.testPlanIteration,
        rootSuiteId: state.testHubRefreshState.rootSuiteId,
        suiteType: suiteType,
        testSuites: state.suiteState.testSuites,
        selectedSuiteId: state.testHubRefreshState.selectedSuiteId,
        selectedSuiteName: state.testHubRefreshState.selectedSuiteName,
        isLoading: state.testHubRefreshState.isLoading,
        isBusy: state.testHubRefreshState.isBusy,
        isAdvancedLicenseEnabled: state.testHubRefreshState.isAdvancedExtensionEnabled,
        isInGridView: state.testHubRefreshState.isInGridView,
        isGridDirty: state.testHubRefreshState.isGridDirty,
        splitterWidth: state.testHubRefreshState.splitterWidth,
        idToTestSuiteMap: state.suiteState.idToTestSuiteMap,
        isShowTestsFromChildSuitesEnabled: state.testHubRefreshState.isShowTestsFromChildSuitesEnabled,
        mineAllPageUrl: state.testHubRefreshState.mineAllPageUrl,
        isPreviewEnabled: previewStatus === PreviewSplitterOptions.RIGHT
    };
}

export const TestPlansHubRefreshConnectedView = connect(mapStateToProps, mapDispatchToProps)(TestPlansHubRefreshView);
